import React, { useEffect, useState, useCallback } from "react";
import api from "../api/axios";
import {
  FaUniversity, FaCheckCircle, FaTimesCircle, FaFilter,
  FaCloudUploadAlt, FaFileAlt, FaExchangeAlt, FaSearch,
  FaSyncAlt, FaDownload
} from "react-icons/fa";

const RECON_MODES = ["Bank Transfer", "Cheque"];

// ─── tiny helpers ────────────────────────────────────────────────────────────
const fmtCurrency = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "AED" }).format(v || 0);

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const StatusBadge = ({ reconciled }) =>
  reconciled ? (
    <span style={{
      background: "#d4edda", color: "#155724", fontSize: "0.78rem",
      padding: "3px 10px", borderRadius: "12px", fontWeight: 600,
    }}>
      <FaCheckCircle style={{ marginRight: 4 }} />Reconciled
    </span>
  ) : (
    <span style={{
      background: "#fff3cd", color: "#856404", fontSize: "0.78rem",
      padding: "3px 10px", borderRadius: "12px", fontWeight: 600,
    }}>
      ⏳ Pending
    </span>
  );
// ─────────────────────────────────────────────────────────────────────────────

export default function BankReconciliation({ defaultTab = "transactions" }) {
  const [activeTab, setActiveTab] = useState(defaultTab);

  // ── Transactions tab state ─────────────────────────────────────────────────
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingTxns, setLoadingTxns] = useState(false);
  const [txnError, setTxnError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("unreconciled");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [reconWorking, setReconWorking] = useState({});

  // ── PDF tab state ──────────────────────────────────────────────────────────
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfDragging, setPdfDragging] = useState(false);
  const [pdfRows, setPdfRows] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfStats, setPdfStats] = useState(null);
  const [pdfReconWorking, setPdfReconWorking] = useState({});

  // ─── Load transactions ────────────────────────────────────────────────────
  const loadTxns = useCallback(async () => {
    setLoadingTxns(true);
    setTxnError(null);
    try {
      const params = new URLSearchParams({ status: statusFilter });
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      const res = await api.get(`reconciliation/transactions?${params}`);
      setTxns(res.data.transactions || []);
      setSummary(res.data.summary || null);
    } catch (e) {
      setTxnError("Failed to load transactions.");
      console.error(e);
    } finally {
      setLoadingTxns(false);
    }
  }, [statusFilter, startDate, endDate]);

  useEffect(() => {
    if (activeTab === "transactions") loadTxns();
  }, [activeTab, loadTxns]);

  // ─── Reconcile / Unreconcile ──────────────────────────────────────────────
  const handleReconcile = async (txn) => {
    const key = `${txn.txn_type}-${txn.id}`;
    setReconWorking((p) => ({ ...p, [key]: true }));
    try {
      const action = txn.is_reconciled ? "unreconcile" : "reconcile";
      const type = txn.txn_type.toLowerCase();
      await api.patch(`reconciliation/${action}/${type}/${txn.id}`);
      await loadTxns();
    } catch (e) {
      console.error(e);
      alert("Action failed. Please try again.");
    } finally {
      setReconWorking((p) => ({ ...p, [key]: false }));
    }
  };

  // ─── Filtered display list ────────────────────────────────────────────────
  const displayTxns = txns.filter((t) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (t.voucher_no || "").toLowerCase().includes(q) ||
      (t.party || "").toLowerCase().includes(q) ||
      (t.payment_mode || "").toLowerCase().includes(q) ||
      (t.bank_name || "").toLowerCase().includes(q)
    );
  });

  // ─── PDF upload handlers ──────────────────────────────────────────────────
  const handleDrop = (e) => {
    e.preventDefault();
    setPdfDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === "application/pdf") setPdfFile(file);
    else alert("Please drop a PDF file.");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setPdfFile(file);
  };

  const handleUpload = async () => {
    if (!pdfFile) return;
    setPdfLoading(true);
    setPdfError(null);
    setPdfRows([]);
    try {
      const formData = new FormData();
      formData.append("file", pdfFile);
      const res = await api.post("reconciliation/upload-statement", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPdfRows(res.data.rows || []);
      setPdfStats({
        total: res.data.total_rows,
        matched: res.data.matched_count,
        unmatched: res.data.unmatched_count,
      });
    } catch (e) {
      setPdfError("Failed to parse PDF. Please ensure it is a digital (not scanned) bank statement.");
      console.error(e);
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfReconcile = async (row) => {
    if (!row.match) return;
    const key = `${row.match.txn_type}-${row.match.id}`;
    setPdfReconWorking((p) => ({ ...p, [key]: true }));
    try {
      await api.patch(`reconciliation/reconcile/${row.match.txn_type}/${row.match.id}`);
      setPdfRows((prev) =>
        prev.map((r) =>
          r.match && r.match.id === row.match.id && r.match.txn_type === row.match.txn_type
            ? { ...r, match: { ...r.match, reconciled: true } }
            : r
        )
      );
    } catch (e) {
      console.error(e);
      alert("Failed to reconcile transaction.");
    } finally {
      setPdfReconWorking((p) => ({ ...p, [key]: false }));
    }
  };

  // ─── Bulk reconcile all matched PDF rows ──────────────────────────────────
  const handleBulkReconcile = async () => {
    const matched = pdfRows.filter((r) => r.match && !r.match.reconciled);
    if (!matched.length) { alert("No unreconciled matches to process."); return; }
    const items = matched.map((r) => ({ txn_type: r.match.txn_type, id: r.match.id }));
    try {
      await api.post("reconciliation/bulk-reconcile", items);
      setPdfRows((prev) =>
        prev.map((r) =>
          r.match ? { ...r, match: { ...r.match, reconciled: true } } : r
        )
      );
      alert(`✓ ${matched.length} transaction(s) reconciled successfully.`);
    } catch (e) {
      console.error(e);
      alert("Bulk reconciliation failed.");
    }
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const tabStyle = (active) => ({
    padding: "10px 24px",
    border: "none",
    borderBottom: active ? "3px solid #0077cc" : "3px solid transparent",
    background: "transparent",
    fontWeight: active ? 700 : 500,
    color: active ? "#0077cc" : "#555",
    cursor: "pointer",
    fontSize: "0.95rem",
    transition: "all 0.2s",
  });

  const cardStyle = {
    background: "#fff",
    borderRadius: 12,
    padding: "16px 20px",
    boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
    textAlign: "center",
  };

  return (
    <div style={{ padding: "24px", fontFamily: "Inter, sans-serif" }}>
      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{
          background: "linear-gradient(135deg,#0077cc,#00aaff)",
          borderRadius: 10, padding: "10px 14px", color: "#fff", fontSize: 22,
        }}>
          <FaUniversity />
        </div>
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, color: "#111" }}>Bank Reconciliation</h2>
          <p style={{ margin: 0, color: "#666", fontSize: "0.9rem" }}>
            Reconcile Bank Transfer &amp; Cheque transactions before they reflect in reports
          </p>
        </div>
      </div>

      {/* ── Summary Cards ────────────────────────────────────────────────── */}
      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
          <div style={cardStyle}>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>Total Transactions</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#0077cc" }}>{summary.total}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>Unreconciled</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#e67e22" }}>{summary.unreconciled_count}</div>
            <div style={{ fontSize: 12, color: "#999" }}>{fmtCurrency(summary.unreconciled_amount)}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>Reconciled</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#27ae60" }}>{summary.reconciled_count}</div>
            <div style={{ fontSize: 12, color: "#999" }}>{fmtCurrency(summary.reconciled_amount)}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>Pending Amount</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#c0392b" }}>
              {fmtCurrency(summary.unreconciled_amount)}
            </div>
          </div>
        </div>
      )}

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div style={{ borderBottom: "1px solid #e0e0e0", marginBottom: 24, display: "flex", gap: 0 }}>
        <button style={tabStyle(activeTab === "transactions")} onClick={() => setActiveTab("transactions")}>
          <FaExchangeAlt style={{ marginRight: 8 }} />Transactions
        </button>
        <button style={tabStyle(activeTab === "upload")} onClick={() => setActiveTab("upload")}>
          <FaCloudUploadAlt style={{ marginRight: 8 }} />Upload Bank Statement (PDF)
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* TAB 1: TRANSACTIONS                                               */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "transactions" && (
        <div>
          {/* Filters */}
          <div style={{
            display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end",
            background: "#f8faff", borderRadius: 10, padding: "16px 20px", marginBottom: 20,
          }}>
            {/* Status */}
            <div>
              <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>
                <FaFilter style={{ marginRight: 4 }} />Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}
              >
                <option value="all">All</option>
                <option value="unreconciled">Unreconciled</option>
                <option value="reconciled">Reconciled</option>
              </select>
            </div>

            {/* Date from */}
            <div>
              <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>From</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }} />
            </div>

            {/* Date to */}
            <div>
              <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>To</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }} />
            </div>

            {/* Search */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>
                <FaSearch style={{ marginRight: 4 }} />Search
              </label>
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search party, voucher, bank…"
                style={{ width: "100%", padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }} />
            </div>

            <button
              onClick={loadTxns}
              style={{
                background: "#0077cc", color: "#fff", border: "none",
                borderRadius: 8, padding: "8px 20px", fontWeight: 600,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              }}
            >
              <FaSyncAlt />Apply
            </button>
          </div>

          {txnError && <div className="alert alert-danger">{txnError}</div>}

          {loadingTxns ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <div className="spinner-border text-primary" role="status" />
              <p style={{ marginTop: 12, color: "#666" }}>Loading transactions…</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto", borderRadius: 10, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                <thead>
                  <tr style={{ background: "#f0f7ff", borderBottom: "2px solid #d0e8ff" }}>
                    {["Voucher No", "Date", "Type", "Party", "Mode", "Bank / Cheque No", "Amount", "Status", "Action"].map((h) => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: "#0077cc", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayTxns.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: 32, color: "#999" }}>
                        No transactions found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    displayTxns.map((t, i) => {
                      const key = `${t.txn_type}-${t.id}`;
                      const working = reconWorking[key];
                      return (
                        <tr key={key} style={{
                          borderBottom: "1px solid #f0f0f0",
                          background: i % 2 === 0 ? "#fff" : "#fafcff",
                          transition: "background 0.15s",
                        }}>
                          <td style={{ padding: "10px 14px", fontWeight: 600, color: "#0077cc" }}>
                            {t.voucher_no}
                          </td>
                          <td style={{ padding: "10px 14px" }}>{fmtDate(t.date)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <span style={{
                              background: t.txn_type === "Receipt" ? "#e8f4fd" : "#fdf0e8",
                              color: t.txn_type === "Receipt" ? "#0077cc" : "#e67e22",
                              borderRadius: 6, padding: "2px 8px", fontSize: "0.8rem", fontWeight: 600,
                            }}>
                              {t.txn_type}
                            </span>
                          </td>
                          <td style={{ padding: "10px 14px" }}>{t.party || "—"}</td>
                          <td style={{ padding: "10px 14px" }}>{t.payment_mode}</td>
                          <td style={{ padding: "10px 14px", fontSize: "0.82rem" }}>
                            {t.bank_name && <div style={{ color: "#333" }}>🏦 {t.bank_name}</div>}
                            {t.cheque_number && <div style={{ color: "#888" }}>Chq: {t.cheque_number}</div>}
                          </td>
                          <td style={{ padding: "10px 14px", fontWeight: 700 }}>{fmtCurrency(t.amount)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <StatusBadge reconciled={t.is_reconciled} />
                          </td>
                          <td style={{ padding: "10px 14px" }}>
                            <button
                              disabled={working}
                              onClick={() => handleReconcile(t)}
                              style={{
                                border: "none", borderRadius: 8, padding: "6px 14px",
                                fontWeight: 600, cursor: working ? "wait" : "pointer",
                                fontSize: "0.82rem",
                                background: t.is_reconciled
                                  ? "linear-gradient(135deg,#f5c6cb,#f8d7da)"
                                  : "linear-gradient(135deg,#c3e6cb,#d4edda)",
                                color: t.is_reconciled ? "#721c24" : "#155724",
                                transition: "all 0.2s",
                              }}
                            >
                              {working
                                ? "…"
                                : t.is_reconciled
                                ? "✗ Unreconcile"
                                : "✓ Reconcile"}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* TAB 2: UPLOAD PDF                                                 */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "upload" && (
        <div>
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setPdfDragging(true); }}
            onDragLeave={() => setPdfDragging(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${pdfDragging ? "#0077cc" : "#b0c8e0"}`,
              borderRadius: 14, padding: "48px 20px", textAlign: "center",
              background: pdfDragging ? "#eaf4ff" : "#f8faff",
              marginBottom: 24, transition: "all 0.2s",
            }}
          >
            <FaCloudUploadAlt style={{ fontSize: 48, color: "#0077cc", marginBottom: 12 }} />
            <p style={{ margin: 0, fontWeight: 600, color: "#0077cc", fontSize: "1.05rem" }}>
              Drag &amp; Drop your bank statement PDF here
            </p>
            <p style={{ margin: "6px 0 16px", color: "#888", fontSize: "0.88rem" }}>
              Supports digital PDF bank statements. Scanned PDFs may have limited accuracy.
            </p>
            <label style={{
              background: "#0077cc", color: "#fff", borderRadius: 8,
              padding: "8px 22px", cursor: "pointer", fontWeight: 600, fontSize: "0.92rem",
            }}>
              Browse File
              <input type="file" accept=".pdf" onChange={handleFileChange} style={{ display: "none" }} />
            </label>
            {pdfFile && (
              <div style={{ marginTop: 14, color: "#333", fontSize: "0.9rem" }}>
                <FaFileAlt style={{ marginRight: 6, color: "#0077cc" }} />
                <strong>{pdfFile.name}</strong> ({(pdfFile.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </div>

          {pdfFile && (
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <button
                onClick={handleUpload}
                disabled={pdfLoading}
                style={{
                  background: "linear-gradient(135deg,#0077cc,#00aaff)",
                  color: "#fff", border: "none", borderRadius: 10,
                  padding: "10px 32px", fontWeight: 700, fontSize: "1rem",
                  cursor: pdfLoading ? "wait" : "pointer",
                }}
              >
                {pdfLoading ? "Parsing PDF…" : "Upload & Parse Statement"}
              </button>
            </div>
          )}

          {pdfError && (
            <div style={{
              background: "#f8d7da", color: "#721c24", borderRadius: 8,
              padding: "12px 16px", marginBottom: 16,
            }}>
              {pdfError}
            </div>
          )}

          {/* Stats bar */}
          {pdfStats && (
            <div style={{
              display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap",
            }}>
              {[
                { label: "Total Rows Parsed", value: pdfStats.total, color: "#0077cc" },
                { label: "Auto-Matched", value: pdfStats.matched, color: "#27ae60" },
                { label: "No Match", value: pdfStats.unmatched, color: "#e67e22" },
              ].map((s) => (
                <div key={s.label} style={{ ...cardStyle, flex: 1, minWidth: 140 }}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
                </div>
              ))}
              {pdfStats.matched > 0 && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <button
                    onClick={handleBulkReconcile}
                    style={{
                      background: "linear-gradient(135deg,#27ae60,#2ecc71)",
                      color: "#fff", border: "none", borderRadius: 10,
                      padding: "10px 24px", fontWeight: 700, cursor: "pointer",
                      fontSize: "0.92rem",
                    }}
                  >
                    <FaCheckCircle style={{ marginRight: 8 }} />
                    Reconcile All Matched ({pdfStats.matched})
                  </button>
                </div>
              )}
            </div>
          )}

          {/* PDF rows table */}
          {pdfRows.length > 0 && (
            <div style={{ overflowX: "auto", borderRadius: 10, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.86rem" }}>
                <thead>
                  <tr style={{ background: "#f0f7ff", borderBottom: "2px solid #d0e8ff" }}>
                    {["Date", "Description", "Debit", "Credit", "Matched Transaction", "Match Voucher", "Action"].map((h) => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: "#0077cc", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pdfRows
                    .filter((r) => r.date || r.description)
                    .map((row, i) => {
                      const matchKey = row.match
                        ? `${row.match.txn_type}-${row.match.id}`
                        : null;
                      const working = matchKey ? pdfReconWorking[matchKey] : false;
                      const alreadyReconciled = row.match?.reconciled;

                      return (
                        <tr key={i} style={{
                          borderBottom: "1px solid #f0f0f0",
                          background: i % 2 === 0 ? "#fff" : "#fafcff",
                        }}>
                          <td style={{ padding: "10px 14px" }}>{row.date || "—"}</td>
                          <td style={{ padding: "10px 14px", maxWidth: 200 }}>
                            {row.description || "—"}
                          </td>
                          <td style={{ padding: "10px 14px", color: "#c0392b", fontWeight: 600 }}>
                            {row.debit != null ? fmtCurrency(row.debit) : "—"}
                          </td>
                          <td style={{ padding: "10px 14px", color: "#27ae60", fontWeight: 600 }}>
                            {row.credit != null ? fmtCurrency(row.credit) : "—"}
                          </td>
                          <td style={{ padding: "10px 14px" }}>
                            {row.match ? (
                              <div>
                                <span style={{
                                  background: alreadyReconciled ? "#d4edda" : "#e8f4fd",
                                  color: alreadyReconciled ? "#155724" : "#0077cc",
                                  borderRadius: 6, padding: "2px 8px", fontSize: "0.78rem", fontWeight: 600,
                                }}>
                                  {alreadyReconciled ? "✓ Done" : "🔗 Match Found"}
                                </span>
                                <div style={{ fontSize: "0.8rem", color: "#555", marginTop: 4 }}>
                                  {row.match.party} — {fmtCurrency(row.match.amount)}
                                </div>
                                <div style={{ fontSize: "0.75rem", color: "#888" }}>
                                  ±{row.match.days_diff} day(s) difference
                                </div>
                              </div>
                            ) : (
                              <span style={{ color: "#999", fontSize: "0.82rem" }}>No match</span>
                            )}
                          </td>
                          <td style={{ padding: "10px 14px", fontWeight: 600, color: "#0077cc" }}>
                            {row.match?.voucher_no || "—"}
                          </td>
                          <td style={{ padding: "10px 14px" }}>
                            {row.match && !alreadyReconciled ? (
                              <button
                                disabled={working}
                                onClick={() => handlePdfReconcile(row)}
                                style={{
                                  background: "linear-gradient(135deg,#c3e6cb,#d4edda)",
                                  color: "#155724", border: "none", borderRadius: 8,
                                  padding: "5px 14px", fontWeight: 600, cursor: "pointer",
                                  fontSize: "0.8rem",
                                }}
                              >
                                {working ? "…" : "✓ Reconcile"}
                              </button>
                            ) : row.match && alreadyReconciled ? (
                              <span style={{ color: "#27ae60", fontSize: "0.82rem" }}>✓ Done</span>
                            ) : (
                              <span style={{ color: "#ccc", fontSize: "0.82rem" }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
